<template>
  <Modal
    name="BULK_CHANGE_APPOINTMENT_STATUS_MODAL"
    width="500"
    :title="$t('appointment.changeStatusModal.title')"
    ref="bulkChangeStatusModal"
    actions
  >
    <template v-slot:body>
      <v-progress-linear
        class="mt-5 mb-7"
        v-show="isAppointmentLoading"
        indeterminate
        rounded
      />

      <div v-show="!isAppointmentLoading">
        <v-form ref="appointmentFormChangeStatus" lazy-validation class="mb-6">
          <v-autocomplete
            v-model="status"
            :label="$t('appointment.adminActions.changeStatus.selectStatus')"
            :no-data-text="$t('appointment.filters.noData')"
            :items="appointmentStatus"
            autocomplete="one-time-code"
            hide-details="auto"
            item-value="value"
            item-text="name"
            clearable
            outlined
            small
          />
        </v-form>
        <div>
          <v-alert v-if="isUnableToChangeStatus" class="red lighten-5 mt-2 mb-6">
            <p class="body-2 red--text mb-2">
              {{ $t('appointment.changeStatusModal.unableToChangeStatus') }}
            </p>
            <v-chip v-for="(item, index) in getAppointmentsUnableToChangeStatus" :key="index" class="mr-1 mb-1 red" x-small>
              <span class="white--text">
                {{ item.code }}
              </span>
            </v-chip>
          </v-alert>

          <p class="body-2 mb-2" v-if="canChangeStatus">
            {{ $t('appointment.changeStatusModal.changeStatusMessage', { quantity: getAppointmentsAbleToChangeStatus.length }) }}
          </p>
          <v-chip v-for="(item, index) in getAppointmentsAbleToChangeStatus" :key="index" class="mr-1 mb-1 orange" x-small>
            <span class="white--text">
              {{ item.code }}
            </span>
          </v-chip>
        </div>
      </div>
    </template>

    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        :disabled="loading"
        elevation="0"
        color="grey"
        class="mr-3"
        large
        text
      >
        {{ $t('appointment.changeStatusModal.cancel') }}
      </v-btn>
      <v-btn
        @click="changeAppointmentStatus"
        :loading="loading"
        :disabled="!canChangeStatus"
        elevation="0"
        large
      >
        {{ $t('appointment.changeStatusModal.change') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex'
import { api } from '@/services'

import Modal from '../../app/Modal.vue'

export default {
  name: 'ModalBulkChangeAppointmentStatus',

  components: {
    Modal,
  },

  props: {
    selectedAppointments: {
      type: Array,
      required: true,
    },

    mapAction: {
      type: Boolean,
      required: false,
      default: true,
    }
  },

  computed: {
    ...mapGetters(['isAppointmentLoading']),

    isUnableToChangeStatus() {
      return this.getAppointmentsUnableToChangeStatus && this.getAppointmentsUnableToChangeStatus.length > 0
    },

    canChangeStatus() {
      return this.status != null && this.getAppointmentsAbleToChangeStatus && this.getAppointmentsAbleToChangeStatus.length > 0
    },

    getAppointmentsUnableToChangeStatus() {
      return this.selectedAppointments.filter(appointment => !this.shouldBeAbleToChangeStatus(appointment))
    },

    getAppointmentsAbleToChangeStatus() {
      return this.selectedAppointments.filter(appointment => this.shouldBeAbleToChangeStatus(appointment))
    },

    isAdminOrPartner() {
      const roles = localStorage.getItem('access_roles')
      const userRoles = roles?.split(',')
      return userRoles?.includes('ROLE_ADMIN') || userRoles?.includes('ROLE_PARTNER')
    },
  },

  methods: {
    ...mapActions(['showSuccessMessage', 'showErrorMessage']),
    ...mapMutations(['closeAllModals', 'setAppointment']),

    createPayload() {
      return {
        status: this.status,
        appointment_ids: this.getAppointmentsAbleToChangeStatus.map(appointment => appointment.id),
      }
    },

    async changeAppointmentStatus() {
      this.loading = true

      const userPath = this.isAdminOrPartner ? 'admin' : 'web'

      try {
        await api.post(`${userPath}/appointment/bulkChangeStatus`, this.createPayload())
        this.showSuccessMessage(this.$t('appointment.changeStatusModal.successfullyChanged'))
        if (this.mapAction) {
          this.setAppointment({})
          this.$emit('refreshMap')
        }
        this.closeAllModals()
      } catch ({ data }) {
        this.showErrorMessage(data?.message || this.$t('appointment.changeStatusModal.error'))
      } finally {
        this.loading = false
      }
    },

    shouldBeAbleToChangeStatus(appointment) {
      if (this.status == 'OPEN' && !this.canSelectOpen(appointment)) {
        return false
      }

      if (this.status == 'SCHEDULED' && !this.canSelectScheduled(appointment)) {
        return false
      }

      if (this.status == 'DRAFT' && !this.canSelectDraft(appointment)) {
        return false
      }

      return true
    },

    canSelectOpen(appointment) {
      return appointment.status != 'OPEN'
    },

    canSelectScheduled(appointment) {
      return ['DONE', 'CANCELED'].includes(appointment.status) && appointment.host
    },

    canSelectDraft(appointment) {
      return ['OPEN', 'REJECTED'].includes(appointment.status) && !appointment.host && (!appointment.group_code && !appointment.group)
    },
  },

  data() {
    return {
      loading: false,
      status: null,
      appointmentStatus: [
        { value: 'OPEN', name: this.$t('appointment.status.OPEN') },
        { value: 'SCHEDULED', name: this.$t('appointment.status.SCHEDULED') },
        { value: 'DRAFT', name: this.$t('appointment.status.DRAFT') },
      ],
    }
  },

  mounted() {
    if (this.mapAction) {
      this.$watch(
        () => this.$refs.bulkChangeStatusModal.dialog,
        (isOpen) => {
          if (isOpen) {
            if (this.selectedAppointments >= 1) {
              this.setAppointment({})
            }
            this.status = null
          }

          if(!isOpen) {
            this.setAppointment({})
          }
        }
      )
    }
  }
}
</script>
